import type { VisualElement } from "@judo/model-api";
import { useCallback, useRef, useSyncExternalStore } from "react";
import { useDataStoreOptional, useVisualPropertyOverrides } from "../contexts";
import { usePageContextOptional } from "../contexts";
import type { DataStoreState } from "../store";
import { deepEqual } from "../utils";

// No-op store for when DataProvider is not available
const EMPTY_SNAPSHOT = { enabledByValue: undefined };

/**
 * Hook for evaluating the runtime disabled state of any VisualElement.
 *
 * Resolution priority (highest to lowest):
 * 1. VisualPropertyOverrides (customization) — `disabled` field
 * 2. enabledBy dynamic data — if referenced boolean attribute is true, element is **enabled** (not disabled)
 * 3. Static model property — element.disabled
 *
 * Safe to use outside DataProvider/PageProvider — returns static disabled value when context is unavailable.
 *
 * @param element - The VisualElement to evaluate disabled state for
 * @returns `true` if the element should be disabled, `false` otherwise
 */
export function useElementDisabled(element: VisualElement): boolean {
	const store = useDataStoreOptional();
	const pageContext = usePageContextOptional();
	const transferId = pageContext?.transferId ?? "";

	const enabledByAttr = element.enabledBy?.name;

	// Cache for selector result to ensure referential stability
	const cacheRef = useRef<{ enabledByValue: unknown }>({ enabledByValue: undefined });

	const selector = useCallback(
		(state: DataStoreState): { enabledByValue: unknown } => {
			if (!enabledByAttr) {
				return cacheRef.current;
			}
			const data = state.transfers.get(transferId)?.data;
			const newResult = { enabledByValue: data ? data[enabledByAttr] : undefined };

			if (deepEqual(cacheRef.current, newResult)) {
				return cacheRef.current;
			}
			cacheRef.current = newResult;
			return newResult;
		},
		[transferId, enabledByAttr]
	);

	const subscribe = useCallback(
		(cb: () => void) => (store ? store.subscribe(selector, cb) : () => {}),
		[store, selector]
	);
	const getSnapshot = useCallback(() => (store ? store.getSnapshot(selector) : EMPTY_SNAPSHOT), [store, selector]);

	const { enabledByValue } = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

	// Resolve disabled: static → enabledBy dynamic → customization override
	const toBoolean = (value: unknown, defaultValue: boolean): boolean => {
		if (typeof value === "boolean") return value;
		if (value === "true") return true;
		if (value === "false") return false;
		return defaultValue;
	};

	let disabled = toBoolean((element as unknown as Record<string, unknown>).disabled, false);

	// enabledBy: when present, fully controls disabled state
	// referenced boolean true → enabled (disabled=false), false → disabled (disabled=true)
	if (enabledByAttr) {
		disabled = !toBoolean(enabledByValue, false);
	}

	// Customization override wins over everything
	const propertyOverrides = useVisualPropertyOverrides(element);
	if (propertyOverrides?.disabled != null) {
		return propertyOverrides.disabled;
	}

	return disabled;
}
